import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import MetaAdsIcon from "../../assets/meta-logo.svg?react";
import FolderMetaIcon from "../../assets/folder-meta.svg?react";
import {
  IconAffiliate,
  IconBolt,
  IconPlug,
  IconBook2,
  IconBrowser,
  IconChartBar,
  IconChevronDown,
  IconClockHour4,
  IconConfetti,
  IconDiscount,
  IconFlask,
  IconHelpCircle,
  IconHistory,
  IconLayoutDashboard,
  IconLock,
  IconLogout,
  IconMail,
  IconMessageCircle,
  IconPackage,
  IconPaperclip,
  IconSelector,
  IconSend,
  IconSettings,
  IconShieldCheck,
  IconShoppingCartPlus,
  IconUserCheck,
  IconUserSearch,
  IconUsers,
  IconWallet,
  IconX,
} from "@tabler/icons-react";
import { BrandMark, PoweredByReveshare } from "../brand/BrandMark";
import { UserAvatar } from "../common/UserAvatar";
import { useBrandAuth } from "../../hooks/useBrandAuth";
import { useBrandIdentity } from "../../hooks/useBrandIdentity";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import { shouldShowMenuItem } from "../../config/sidebar-permissions";
import { whiteLabelService } from "../../services/whiteLabel.service";
import { getMenuItemDescription } from "../../config/sidebar-feature-descriptions";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useBrand } from "@/contexts/BrandContext/hooks";
import { cn } from "@/lib/utils";
import TikTokIcon from "@/assets/tiktok";
import { useTikTokAccess } from "@/hooks/useTikTokAccess";
import { BetaBadge } from "@/pages/brand/tiktok/pre-release";
import {
  ActiveRail,
  DarkModeRow,
  EdgeToggle,
  ICON,
  RowSkeleton,
  SUB_ICON,
  rowActive,
  rowBase,
  rowIdle,
} from "@/components/navigation/sidebar-kit";

interface SubItem {
  id: string;
  label: string;
  path: string;
  activePrefix?: string;
  icon?: JSX.Element;
  isLocked?: boolean;
  description?: string | null;
}

interface MenuItem {
  id: string;
  label: string;
  path: string;
  /** Route prefix that keeps this item highlighted (e.g. detail pages).
      Defaults to `path`; only needed when siblings share the path's parent. */
  activePrefix?: string;
  icon: JSX.Element;
  isLocked?: boolean;
  description?: string | null;
  subItems?: SubItem[];
  /** A small inline badge (e.g. Beta); the row floats it past the label, or on the icon when collapsed. */
  badge?: ReactNode;
}

interface MenuSection {
  id: string;
  /** Omitted for the top group, which needs no heading. */
  label?: string;
  items: MenuItem[];
}

/** Active when the current route is the given base or nested under it, so
    detail pages keep their section highlighted. */
function routeMatches(pathname: string, base: string) {
  // The dashboard base would otherwise swallow every /brand/* route, so it
  // only matches the index route and its canonical /brand/dashboard URL.
  if (base === "/brand") {
    return (
      pathname === "/brand" ||
      pathname === "/brand/" ||
      pathname === "/brand/dashboard" ||
      pathname.startsWith("/brand/dashboard/")
    );
  }
  return pathname === base || pathname.startsWith(base + "/");
}

const PRICING_PATH = "/brand/settings?tab=pricing";

function LockedItemPopover({
  description,
  children,
}: {
  description: string | null | undefined;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        sideOffset={12}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <div className="space-y-2">
          <p className="text-xs font-semibold text-brand-600">
            Available on higher plans
          </p>
          {description && (
            <div
              className="text-sm text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SubItemRow({
  subItem,
  active,
  onNavigate,
}: {
  subItem: SubItem;
  active: boolean;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const inner = (
    <>
      {subItem.icon && (
        <span className={cn("shrink-0", active && "text-brand-600")}>
          {subItem.icon}
        </span>
      )}
      <span className="flex-1 truncate">{subItem.label}</span>
      {subItem.isLocked && (
        <IconLock size={14} stroke={1.75} className="shrink-0 opacity-70" />
      )}
    </>
  );
  const className = cn(
    rowBase,
    "h-8 w-full gap-2.5 px-2 text-[13px]",
    active ? rowActive : rowIdle,
    subItem.isLocked && "opacity-70"
  );

  if (subItem.isLocked) {
    return (
      <LockedItemPopover description={subItem.description}>
        <button
          id={subItem.id}
          type="button"
          onClick={() => {
            onNavigate?.();
            navigate(PRICING_PATH);
          }}
          className={className}
        >
          {inner}
        </button>
      </LockedItemPopover>
    );
  }
  return (
    <Link id={subItem.id} to={subItem.path} onClick={onNavigate} className={className}>
      {inner}
    </Link>
  );
}

function MenuItemRow({
  item,
  isCollapsed,
}: {
  item: MenuItem;
  isCollapsed: boolean;
}) {
  const { id, label, path, activePrefix, icon, subItems, isLocked, description, badge } = item;
  const location = useLocation();
  const navigate = useNavigate();
  const isCurrentPath = routeMatches(location.pathname, activePrefix ?? path);
  const hasSubItems = !!subItems && subItems.length > 0;
  const activeSub = subItems?.find((s) =>
    routeMatches(location.pathname, s.activePrefix ?? s.path)
  );
  const isActive = isCurrentPath || !!activeSub;
  const [isExpanded, setIsExpanded] = useState(isActive);
  // Collapsed-mode flyout so child items stay reachable.
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  useEffect(() => {
    setIsExpanded(isActive);
  }, [isActive]);

  const iconEl = (
    <span
      className={cn(
        "flex size-5 shrink-0 items-center justify-center [&>svg]:size-5",
        isActive ? "text-brand-600" : "text-current"
      )}
    >
      {icon}
    </span>
  );

  const rowClass = cn(
    rowBase,
    isCollapsed ? "mx-auto size-10 justify-center" : "h-9 w-full gap-3 px-2.5",
    isActive ? rowActive : rowIdle,
    isLocked && "opacity-70"
  );

  // Collapsed: icon-only rows, with a flyout for anything that has children.
  if (isCollapsed) {
    const collapsedRow = isLocked ? (
      <button
        type="button"
        onClick={() => navigate(PRICING_PATH)}
        className={rowClass}
        title={label}
      >
        {isActive && <ActiveRail />}
        {iconEl}
      </button>
    ) : (
      <Link to={path} className={rowClass} title={hasSubItems ? undefined : label}>
        {isActive && <ActiveRail />}
        {iconEl}
        {badge ? <span className="pointer-events-none absolute -right-1.5 -top-1.5 z-10">{badge}</span> : null}
      </Link>
    );

    if (isLocked) {
      return (
        <div id={id} className="relative">
          <LockedItemPopover description={description}>{collapsedRow}</LockedItemPopover>
        </div>
      );
    }
    if (!hasSubItems) {
      return (
        <div id={id} className="relative">
          {collapsedRow}
        </div>
      );
    }
    return (
      <div id={id} className="relative">
        <Popover open={flyoutOpen} onOpenChange={setFlyoutOpen}>
          <PopoverTrigger asChild>
            <span
              onMouseEnter={() => setFlyoutOpen(true)}
              onMouseLeave={() => setFlyoutOpen(false)}
            >
              {collapsedRow}
            </span>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={12}
            className="w-56 !p-1.5"
            onMouseEnter={() => setFlyoutOpen(true)}
            onMouseLeave={() => setFlyoutOpen(false)}
          >
            <Link
              to={path}
              onClick={() => setFlyoutOpen(false)}
              className={cn(
                rowBase,
                "h-8 w-full px-2 text-[13px]",
                isCurrentPath && !activeSub ? rowActive : "text-foreground hover:bg-accent"
              )}
            >
              {label}
            </Link>
            <div className="mt-1 space-y-0.5 border-t border-border pt-1">
              {subItems!.map((subItem) => (
                <SubItemRow
                  key={subItem.id}
                  subItem={subItem}
                  active={activeSub?.id === subItem.id}
                  onNavigate={() => setFlyoutOpen(false)}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  const chevron = hasSubItems && !isLocked && (
    <button
      type="button"
      aria-label={isExpanded ? `Collapse ${label}` : `Expand ${label}`}
      aria-expanded={isExpanded}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded((v) => !v);
      }}
      className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
    >
      <IconChevronDown
        size={16}
        stroke={1.75}
        className={cn("transition-transform duration-200", isExpanded && "rotate-180")}
      />
    </button>
  );

  const expandedRow = isLocked ? (
    <LockedItemPopover description={description}>
      <button type="button" onClick={() => navigate(PRICING_PATH)} className={rowClass}>
        {iconEl}
        <span className="flex-1 truncate text-left">{label}</span>
        <IconLock size={14} stroke={1.75} className="shrink-0 opacity-70" />
      </button>
    </LockedItemPopover>
  ) : (
    <Link to={path} className={rowClass}>
      {isActive && <ActiveRail />}
      {iconEl}
      {badge ? (
        // The badge hangs off the end of the label itself, not the row's far edge.
        <span className="min-w-0 flex-1">
          <span className="relative inline-block max-w-full align-middle">
            <span className="block truncate">{label}</span>
            <span className="pointer-events-none absolute -top-2 left-full ml-1.5 z-10">{badge}</span>
          </span>
        </span>
      ) : (
        <span className="flex-1 truncate">{label}</span>
      )}
      {chevron}
    </Link>
  );

  return (
    <div id={id} className="relative">
      {expandedRow}
      {hasSubItems && isExpanded && !isLocked && (
        <div className="ml-[18px] mt-0.5 space-y-0.5 border-l border-border pl-3">
          {subItems!.map((subItem) => (
            <SubItemRow
              key={subItem.id}
              subItem={subItem}
              active={activeSub?.id === subItem.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** FAQ / Resources / Support / Settings, plus theme and sign out, in the
    account popover so the nav itself never needs to scroll. */
function AccountMenu({
  items,
  onNavigate,
  onLogout,
  showIdentity,
  name,
  email,
}: {
  items: MenuItem[];
  onNavigate: () => void;
  onLogout: () => void;
  showIdentity: boolean;
  name: string;
  email: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="space-y-0.5">
      {showIdentity && (
        <div className="mb-1 border-b border-border px-2 pb-2 pt-1">
          <div className="truncate text-sm font-medium text-foreground">{name}</div>
          <div className="truncate text-xs text-muted-foreground">{email}</div>
        </div>
      )}
      {items.map((item) => {
        const active = routeMatches(location.pathname, item.path);
        return (
          <button
            key={item.id}
            id={item.id}
            type="button"
            onClick={() => {
              onNavigate();
              navigate(item.isLocked ? PRICING_PATH : item.path);
            }}
            className={cn(
              rowBase,
              "h-8 w-full gap-2.5 px-2 text-[13px]",
              active ? rowActive : "text-foreground hover:bg-accent"
            )}
          >
            <span className={cn("[&>svg]:size-4", active ? "text-brand-600" : "text-muted-foreground")}>
              {item.icon}
            </span>
            <span className="flex-1 truncate text-left">{item.label}</span>
            {item.isLocked && (
              <IconLock size={14} stroke={1.75} className="shrink-0 text-muted-foreground" />
            )}
          </button>
        );
      })}
      <DarkModeRow />
      <button
        type="button"
        onClick={onLogout}
        className={cn(rowBase, "h-8 w-full gap-2.5 px-2 text-[13px] text-foreground hover:bg-accent")}
      >
        <IconLogout size={16} stroke={1.75} className="text-muted-foreground" />
        <span className="flex-1 text-left">Sign out</span>
      </button>
    </div>
  );
}

const sections: MenuSection[] = [
  {
    id: "overview",
    items: [
      {
        label: "Dashboard",
        path: "/brand",
        id: "dashboard",
        icon: <IconLayoutDashboard {...ICON} />,
      },
    ],
  },
  {
    id: "grow",
    label: "Grow",
    items: [
      {
        label: "Social Search",
        id: "social-search",
        path: "/brand/social-search",
        icon: <IconUserSearch {...ICON} />,
      },
      {
        label: "TikTok Shop",
        id: "tiktok-shop",
        path: "/brand/tiktok",
        icon: <TikTokIcon className="size-5" />,
        // PRE_RELEASE_TIKTOK
        badge: <BetaBadge size="sm" className="shadow-sm" />,
      },
      {
        label: "Affiliate Programs",
        id: "affiliate-programs",
        path: "/brand/programs",
        icon: <IconAffiliate {...ICON} />,
        subItems: [
          { label: "Analytics", id: "programs-analytics", path: "/brand/programs/analytics", icon: <IconChartBar {...SUB_ICON} /> },
        ],
      },
      {
        label: "Program Page",
        id: "program-page",
        path: "/brand/program-page",
        icon: <IconBrowser {...ICON} />,
      },
      {
        label: "Campaigns",
        id: "campaigns",
        path: "/brand/campaigns",
        icon: <IconDiscount {...ICON} />,
      },
      {
        label: "Meta Ads",
        id: "meta-ads",
        path: "/brand/meta-ads",
        icon: <MetaAdsIcon className="size-5" />,
        subItems: [
          {
            label: "Ad Performance",
            id: "ad-performance",
            path: "/brand/meta-ads/performance",
            icon: <IconChartBar {...ICON} />,
          },
          {
            label: "Content programs",
            id: "content-programs",
            path: "/brand/content-programs",
            icon: <FolderMetaIcon className="size-4" />,
          },
        ],
      },
      {
        label: "Post-checkout",
        id: "post-checkout",
        path: "/brand/post-checkout",
        icon: <IconShoppingCartPlus {...ICON} />,
      },
    ],
  },
  {
    id: "manage",
    label: "Manage",
    items: [
      {
        label: "Affiliates",
        id: "affiliates",
        path: "/brand/affiliates/active",
        activePrefix: "/brand/affiliates",
        icon: <IconUsers {...ICON} />,
        subItems: [
          { label: "Invite", id: "invite", path: "/brand/affiliates/invite", icon: <IconSend {...SUB_ICON} /> },
          { label: "Invited", id: "invited", path: "/brand/affiliates/invited", icon: <IconClockHour4 {...SUB_ICON} /> },
          { label: "Applicants", id: "applicants", path: "/brand/affiliates/applicants", icon: <IconUserCheck {...SUB_ICON} /> },
          { label: "Share Resources", id: "share-resources", path: "/brand/affiliates/share-resources", icon: <IconPaperclip {...SUB_ICON} /> },
          { label: "Test Accounts", id: "test-accounts", path: "/brand/affiliates/test-accounts", icon: <IconFlask {...SUB_ICON} /> },
        ],
      },
      {
        label: "Products",
        id: "products",
        path: "/brand/products/list",
        activePrefix: "/brand/products",
        icon: <IconPackage {...ICON} />,
        subItems: [
          { label: "Analytics", id: "products-analytics", path: "/brand/products/analytics", icon: <IconChartBar {...SUB_ICON} /> },
        ],
      },
      {
        label: "Payouts",
        id: "payouts",
        path: "/brand/payouts",
        icon: <IconWallet {...ICON} />,
        subItems: [
          { label: "History", id: "history", path: "/brand/payouts/history", icon: <IconHistory {...SUB_ICON} /> },
          { label: "Compliance", id: "compliance", path: "/brand/payouts/compliance", icon: <IconShieldCheck {...SUB_ICON} /> },
        ],
      },
      {
        label: "Milestone Rewards",
        id: "rewards",
        path: "/brand/rewards",
        icon: <IconConfetti {...ICON} />,
      },
      {
        label: "Automation",
        id: "automation",
        path: "/brand/automations",
        icon: <IconBolt {...ICON} />,
        subItems: [
          { label: "Email Automation", id: "email-automation", path: "/brand/automations/email", icon: <IconMail {...SUB_ICON} /> },
        ],
      },
      {
        label: "Integrations",
        id: "integrations",
        path: "/brand/integrations",
        icon: <IconPlug {...ICON} />,
      },
    ],
  },
];

const accountItems: MenuItem[] = [
  { label: "FAQ", id: "faq", path: "/brand/faq", icon: <IconHelpCircle {...SUB_ICON} /> },
  { label: "Reveshare Resources", id: "reveshare-resources", path: "/brand/reveshare-resources", icon: <IconBook2 {...SUB_ICON} /> },
  { label: "Support", id: "support", path: "/brand/support", icon: <IconMessageCircle {...SUB_ICON} /> },
  { label: "Settings", id: "settings", path: "/brand/settings", icon: <IconSettings {...SUB_ICON} /> },
];

const totalItemCount = sections.reduce((n, s) => n + s.items.length, 0);

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

// Routes that want the full canvas start with the sidebar collapsed.
const shouldAutoCollapseSidebar = (pathname: string, search: string) =>
  (pathname === "/brand/settings" &&
    new URLSearchParams(search).get("tab") === "appearance-settings") ||
  pathname.startsWith("/brand/social-search");

// The brand layout remounts per navigation (Route key=pathname), so the
// collapsed state must be RIGHT on first paint — initializing to `false` and
// collapsing in an effect makes the sidebar visibly twitch shut on arrival.
const initialSidebarCollapsed = () => {
  const width = window.innerWidth;
  if (width < 768) return false; // mobile: overlay sidebar is always expanded
  if (width < 980) return true; // tablet: collapsed by default
  return shouldAutoCollapseSidebar(
    window.location.pathname,
    window.location.search
  );
};

export default function Sidebar({
  isMobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const { user } = useBrandAuth();
  const { brandDetails } = useBrand();
  const { name: brandName } = useBrandIdentity();
  const { brandAuth } = useAuth();
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  // Program Page is a white-label surface: brands without white-label don't
  // see it at all. Cached, since the layout remounts on every navigation.
  const { data: whiteLabel, isLoading: whiteLabelLoading } = useQuery({
    queryKey: ["white-label", "me"],
    queryFn: whiteLabelService.getMyConfig,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const isWhiteLabelled = !!whiteLabel && whiteLabel.status !== "NONE";
  const menuLoading = permissionsLoading || whiteLabelLoading;
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(initialSidebarCollapsed);
  const [isMobile, setIsMobile] = useState(false);
  const [isManuallyToggled, setIsManuallyToggled] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const tiktokAccess = useTikTokAccess();

  // Mark menu items as locked based on permissions (instead of filtering them out)
  const lockMenuItem = (item: MenuItem): MenuItem => {
    const isLocked = !shouldShowMenuItem(item.id, hasFeature);
    const description = getMenuItemDescription(item.id);
    if (!item.subItems) return { ...item, isLocked, description };
    const subItems = item.subItems
      // test-accounts is dev-only: fully hide it rather than lock it
      .filter((sub) => sub.id !== "test-accounts" || brandDetails?.isDevStore === true)
      .map((sub) => ({
        ...sub,
        isLocked: !shouldShowMenuItem(sub.id, hasFeature),
        description: getMenuItemDescription(sub.id),
      }));
    return { ...item, isLocked, description, subItems };
  };

  const lockedSections = sections.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => item.id !== "program-page" || isWhiteLabelled)
      // TikTok Shop exists only for brands the server lets in (PRE_RELEASE_TIKTOK:
      // the admin pilot today, the plan feature at GA).
      .filter((item) => item.id !== "tiktok-shop" || tiktokAccess.enabled)
      .map(lockMenuItem),
  }));
  const lockedAccountItems = accountItems.map(lockMenuItem);
  const isAccountItemActive = lockedAccountItems.some((item) =>
    routeMatches(location.pathname, item.path)
  );

  const handleLogout = async () => {
    try {
      await brandAuth.logout();
      navigate("/auth/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    setIsManuallyToggled(true);
  };

  // Collapse state derives from viewport + route in ONE effect. The brand
  // routes remount this layout on every navigation (Route key=pathname), so
  // splitting this across two effects lets the later one clobber the
  // route-based collapse on mount.
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth < 768;
      const isTabletView = window.innerWidth < 980;

      setIsMobile(isMobileView);

      if (isMobileView) {
        setIsCollapsed(false); // On mobile, sidebar is always expanded when open
      } else if (isManuallyToggled) {
        // Respect the user's explicit toggle
      } else if (isTabletView) {
        setIsCollapsed(true); // On tablet, sidebar is collapsed by default
      } else {
        // On desktop, collapsed only on routes that want the full canvas
        setIsCollapsed(
          shouldAutoCollapseSidebar(location.pathname, location.search)
        );
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [location.pathname, location.search, isManuallyToggled]);

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && isMobileOpen && onMobileClose) {
        const sidebar = document.getElementById("mobile-sidebar");
        if (sidebar && !sidebar.contains(event.target as Node)) {
          onMobileClose();
        }
      }
    };

    if (isMobile && isMobileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Prevent body scroll when mobile sidebar is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isMobile, isMobileOpen, onMobileClose]);

  const userName = user?.name || "User";
  const userEmail = user?.email || "";

  const renderNav = (collapsed: boolean) => (
    <nav className={cn("py-3", collapsed ? "px-3.5" : "px-2")}>
      {menuLoading
        ? Array.from({ length: totalItemCount }).map((_, i) => (
            <RowSkeleton key={i} collapsed={collapsed} />
          ))
        : lockedSections.map((section, index) => (
            <div key={section.id} className={cn(index > 0 && "mt-4")}>
              {index > 0 &&
                (collapsed ? (
                  <div aria-hidden className="mx-1 mb-2 h-px bg-border" />
                ) : (
                  <div className="mb-1 px-2.5 text-[11px] font-medium text-muted-foreground">
                    {section.label}
                  </div>
                ))}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <MenuItemRow key={item.id} item={item} isCollapsed={collapsed} />
                ))}
              </div>
            </div>
          ))}
    </nav>
  );

  const accountPopover = (collapsed: boolean, side: "top" | "right") => (
    <Popover open={accountOpen} onOpenChange={setAccountOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Account"
          aria-label="Account"
          className={cn(
            "flex items-center rounded-md transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            collapsed ? "mx-auto size-10 justify-center" : "w-full gap-2.5 p-1.5 text-left"
          )}
        >
          <span
            className={cn(
              "flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted",
              isAccountItemActive && "ring-2 ring-brand-600 ring-offset-2 ring-offset-card"
            )}
          >
            <UserAvatar src={user?.profileImage} name={user?.name} className="h-full w-full text-sm" />
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-foreground">
                  {userName}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {userEmail}
                </span>
              </span>
              <IconSelector size={16} stroke={1.75} className="shrink-0 text-muted-foreground" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={side === "top" ? "start" : "end"}
        sideOffset={10}
        className="w-60 !p-1.5"
      >
        <AccountMenu
          items={lockedAccountItems}
          showIdentity={collapsed}
          name={userName}
          email={userEmail}
          onNavigate={() => {
            setAccountOpen(false);
            onMobileClose?.();
          }}
          onLogout={handleLogout}
        />
      </PopoverContent>
    </Popover>
  );

  const header = (collapsed: boolean) => (
    <div className={cn("flex items-center", collapsed ? "justify-center px-3.5 pt-4 pb-2" : "gap-2.5 px-4 pt-4 pb-3")}>
      <BrandMark showName={false} logoClassName="size-9 rounded-lg" />
      {!collapsed && (
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold leading-tight text-foreground">
            {brandName ?? "Your brand"}
          </div>
          <PoweredByReveshare className="justify-start py-0 pt-0.5" />
        </div>
      )}
    </div>
  );

  // Mobile overlay
  if (isMobile) {
    return (
      <div className="no-print">
        {isMobileOpen && (
          <div className="fixed inset-0 z-40 bg-black/50" onClick={onMobileClose} />
        )}
        <aside
          id="mobile-sidebar"
          className={cn(
            "fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-border bg-card transition-transform duration-300",
            isMobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex items-start justify-between pr-2">
            {header(false)}
            <button
              type="button"
              onClick={onMobileClose}
              aria-label="Close menu"
              className="mt-4 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <IconX size={18} stroke={1.75} />
            </button>
          </div>
          <div className="scrollbar-hide flex-1 overflow-y-auto">{renderNav(false)}</div>
          <div className="border-t border-border p-2">{accountPopover(false, "top")}</div>
        </aside>
      </div>
    );
  }

  // Desktop/Tablet sidebar
  return (
    <aside
      className={cn(
        "no-print group/sidebar sticky top-0 left-0 z-30 flex h-screen flex-col border-r border-border bg-card transition-[width] duration-300",
        isCollapsed ? "w-[68px]" : "w-64"
      )}
      onMouseEnter={() => {
        if (window.innerWidth < 980 && window.innerWidth >= 768 && !isManuallyToggled) {
          setIsCollapsed(false);
        }
      }}
      onMouseLeave={() => {
        if (window.innerWidth < 980 && window.innerWidth >= 768 && !isManuallyToggled) {
          setIsCollapsed(true);
        }
      }}
    >
      <EdgeToggle collapsed={isCollapsed} onToggle={toggleCollapse} />

      {header(isCollapsed)}

      <div className="scrollbar-hide flex-1 overflow-y-auto overflow-x-hidden">
        {renderNav(isCollapsed)}
      </div>

      <div className={cn("border-t border-border", isCollapsed ? "px-3.5 py-3" : "p-2")}>
        {accountPopover(isCollapsed, isCollapsed ? "right" : "top")}
      </div>
    </aside>
  );
}
